import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

function headers() {
  const token = sessionStorage.getItem('syrakis_token');
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const SCOPE_OPTIONS = [
  { value: 0, label: 'Sin acceso' },
  { value: 1, label: 'Datos propios o recurso asignado' },
  { value: 2, label: 'Equipo, cartera o unidades asignadas' },
  { value: 3, label: 'Todo el tenant en este feature' },
  { value: 4, label: 'Administración completa de este feature' },
];

const SCOPE_GUIDE = SCOPE_OPTIONS.slice(1);

export default function FeatureRoleMatrixPage() {
  const { featureId } = useParams();
  const location = useLocation();
  const [featureName, setFeatureName] = useState(featureId);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [scopes, setScopes] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(new URLSearchParams(location.search).get('mode') || '');
  const [canEditGlobal, setCanEditGlobal] = useState(false);
  const [maxScopes, setMaxScopes] = useState({});

  useEffect(() => {
    let active = true;
    fetch(`/api/features/${encodeURIComponent(featureId)}/template${mode ? `?mode=${encodeURIComponent(mode)}` : ''}`, { credentials: 'same-origin', headers: headers() })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'No se pudo cargar la plantilla.');
        if (!active) return;
        setFeatureName(data.feature_name || featureId);
        setMode(data.mode || 'local');
        setCanEditGlobal(Boolean(data.can_edit_global));
        setMaxScopes(data.max_scopes || {});
        setRoles(data.roles || []);
        setPermissions(data.permissions || []);
        const next = {};
        (data.assignments || []).forEach((assignment) => {
          next[`${assignment.permission_id}:${assignment.role_slug}`] = Number(assignment.scope_level || 0);
        });
        setScopes(next);
      })
      .catch((reason) => active && setError(reason.message));
    return () => { active = false; };
  }, [featureId, mode]);

  function setScope(permission, role, value) {
    const key = `${permission}:${role}`;
    setScopes((current) => ({ ...current, [key]: Number(value) }));
  }

  function editPermission(id, field, value) {
    setPermissions((current) => current.map((permission) => permission.id === id ? { ...permission, [field]: value } : permission));
  }

  async function save() {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const assignments = roles.map((role) => ({
        role_slug: role.slug,
        permissions: permissions
          .filter((permission) => Number(scopes[`${permission.id}:${role.slug}`] || 0) > 0)
          .map((permission) => ({ id: permission.id, scope: Number(scopes[`${permission.id}:${role.slug}`]) })),
      }));
      const response = await fetch(`/api/features/${encodeURIComponent(featureId)}/template`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: headers(),
        body: JSON.stringify({ mode, permissions: mode === 'global' ? permissions : [], assignments }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo guardar la plantilla.');
      setMessage(data.message || 'Plantilla guardada.');
    } catch (reason) {
      setError(reason.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dashboard-main-body">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h4 className="mb-8">Plantilla de roles: {featureName}</h4>
          <p className="text-secondary-light mb-0">{mode === 'local' ? 'Ajusta la plantilla de este tenant. Cada cambio queda limitado a los permisos y alcances de tu rol.' : 'Define la plantilla global que Core inyecta en todos los tenants activos.'}</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {canEditGlobal && <select className="form-select" value={mode} onChange={(event) => { setMode(event.target.value); setError(''); setMessage(''); }} aria-label="Alcance de plantilla RBAC"><option value="global">Plantilla global</option><option value="local">Plantilla de este tenant</option></select>}
          <Link to="/features" className="btn btn-light">Volver</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <section className="card p-24 mb-20">
        <h6 className="mb-8">Guía de scopes</h6>
        <p className="text-secondary-light">El permiso define la acción. El scope limita el alcance máximo de esa acción dentro de este feature; no concede permisos por sí solo ni habilita acceso a otros features.</p>
        <div className="row g-3">
          {SCOPE_GUIDE.map((scope) => (
            <div className="col-md-6 col-xl-3" key={scope.value}>
              <div className="border rounded-3 p-16 h-100 bg-neutral-50">
                <span className="fw-semibold">{scope.label}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="alert alert-warning mt-16 mb-0">El nombre del scope es una guía común. Cada feature debe definir y aplicar el filtro real de datos —por ejemplo, recurso asignado, unidad organizativa o tenant— en su backend. Un scope amplio sigue limitado a este feature.</div>
      </section>

      <div className="card">
        <div className="table-responsive">
          <table className="table bordered-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ minWidth: 320 }}>Permiso / acción</th>
                {roles.map((role) => <th key={role.slug} className="text-center">{role.nombre || role.slug}</th>)}
              </tr>
            </thead>
            <tbody>
              {permissions.map((permission) => (
                <tr key={permission.id}>
                  <td>
                    <input className="form-control form-control-sm mb-8" value={permission.id} disabled aria-label="Identificador del permiso" />
                    <input className="form-control form-control-sm mb-8" value={permission.nombre || ''} onChange={(event) => editPermission(permission.id, 'nombre', event.target.value)} placeholder="Nombre del permiso" aria-label="Nombre del permiso" />
                    <textarea className="form-control form-control-sm" rows="2" value={permission.descripcion || ''} onChange={(event) => editPermission(permission.id, 'descripcion', event.target.value)} placeholder="Descripción de la acción" aria-label="Descripción del permiso" />
                  </td>
                  {roles.map((role) => (
                    <td key={role.slug} className="text-center" style={{ minWidth: 240 }}>
                      <select className="form-select form-select-sm" value={scopes[`${permission.id}:${role.slug}`] || 0} onChange={(event) => setScope(permission.id, role.slug, event.target.value)} aria-label={`Scope de ${permission.nombre || permission.id} para ${role.nombre || role.slug}`}>
                        {SCOPE_OPTIONS.map((scope) => <option key={scope.value} value={scope.value} disabled={mode === 'local' && !canEditGlobal && scope.value > Number(maxScopes[permission.id] || 0)}>{scope.label}</option>)}
                      </select>
                    </td>
                  ))}
                </tr>
              ))}
              {permissions.length === 0 && <tr><td colSpan={roles.length + 1} className="text-center text-secondary-light py-24">Esta feature no declara permisos en su plantilla.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="card-body p-24">
          <button className="btn btn-primary" onClick={save} disabled={saving || roles.length === 0}>
            {saving ? 'Guardando plantilla...' : 'Guardar plantilla de permisos'}
          </button>
        </div>
      </div>
    </div>
  );
}
